import React, { useCallback } from 'react';
import {
  AhaEditorUtils,
  MigrateToV2,
  type AhaEditor,
  type LayoutComponent,
  type EditorPlugin,
  type DocumentValue,
} from '@aha-app/aha-editor-v2';
import DraftManager from '../../DraftManager';
import { ApplicationView, observable, raw } from '@aha-app/mvc';
import { isEqual } from 'lodash';

export interface Draft {
  value: DocumentValue | null;
}

export interface DraftManagerDelegate {
  keyPrefix: string;
  keyValue?: string;
}

function isEmptyValue(value: DocumentValue) {
  if (!value.children) return true;

  return isEqual(
    value.children,
    AhaEditorUtils.Editor.emptyDocument().children
  );
}

export interface DraftManagerProps {
  customFieldDefinitionId?: RecordId;
  name?: string;
  saveDrafts?: boolean;
}

function documentChanged(previousDocument, newDocument) {
  return !isEqual(previousDocument, newDocument);
}

const DraftManagerPluginFactory = (
  delegate: DraftManagerDelegate
): EditorPlugin => {
  const draft = observable<Draft>({
    value: null,
  });

  let ahaEditor: AhaEditor;
  const DraftManagerComponent: LayoutComponent = () => {
    const { keyPrefix, keyValue } = delegate;

    const restoreDraft = useCallback((newDraftProxiedValue: DocumentValue) => {
      if (!ahaEditor) return;

      const draftValue = raw(newDraftProxiedValue);
      if (draftValue === null) return;

      // It's possible that a draft was saved for v1 and it's
      // being loaded in v2.
      const value = MigrateToV2(draftValue);

      ahaEditor.replaceContentWithoutCollaboration(value);
    }, []);

    const undoRestoreDraft = useCallback(() => {
      restoreDraft(AhaEditorUtils.Editor.emptyDocument());
    }, [restoreDraft]);

    if (!draft.value) return null;

    return (
      <DraftManager
        active
        saveValueChanges
        key={`draft-manager-${keyPrefix}${keyValue ?? ''}`}
        value={draft.value}
        onRestoreDraft={restoreDraft}
        onUndoDraft={undoRestoreDraft}
        keyPrefix={keyPrefix}
        keyValue={keyValue}
        isEmptyValue={isEmptyValue}
        documentChanged={documentChanged}
      />
    );
  };

  const DraftManagerPlugin: EditorPlugin = {
    name: 'Draft manager layout plugin',
    handlers: {
      onInit(_ahaEditor: AhaEditor) {
        ahaEditor = _ahaEditor;
      },
      onMount(ahaEditor) {
        const { editor } = ahaEditor;
        draft.value = {
          children: editor.children,
          metadata: editor.metadata,
        };
      },
      onChange(ahaEditor: AhaEditor) {
        const { editor } = ahaEditor;
        draft.value = {
          children: editor.children,
          metadata: editor.metadata,
        };
      },
    },
    layoutComponent: {
      location: 'below',
      Component: ApplicationView(DraftManagerComponent),
    },
  };

  return DraftManagerPlugin;
};

export default DraftManagerPluginFactory;
